import React, { FC, useEffect, useState } from 'react';
import { usePlayer } from 'providers/player/PlayerContext';
import { getMiniPlayerProps } from 'utils/fnData';
import { PlayingAssetType } from 'types/Player';
import { useNavigate } from 'react-router';
import { MILLISECONDS } from 'utils/TimeUnit';
import useYouboraContentTracking, { ContentTrackingParams } from 'hooks/useYouboraContentTracking';
import { Broadcast, NowAndNext } from 'types/Asset';
import { getLivePlayerURL, getNowAndNextDetailsURL } from 'utils/fnUrl';
import { LiveProgressIndicator } from 'components/LiveProgressIndicator/LiveProgressIndicator';
import { useDetailLocationParams } from 'hooks/useDetailLocationParams';
import { useAuth } from 'providers/useAuth/AuthContext';
import { StandardCover } from 'types/Cover';
import { CardTrackingData } from 'types/Tracking';
import { getNowAndNextCardContentMarkerValue } from 'utils/fnContentMarker';
import { CardContext } from 'types/Common';
import { useChannels } from 'providers/useChannels/useChannels';
import { useApp } from 'providers/useApp/AppContext';
import { matchIsMobileDevice } from 'style/styled-components/cssMediaQueries';
import { Card } from 'components/Cards/Card/Card';
import { CardVariant } from 'components/Cards/Card/cardVariants';

const channelsFetchInitiated = { current: false };

interface NowAndNextCardPresenterProps {
    asset: NowAndNext;
    trackingData: CardTrackingData;
    cardContext: CardContext;
    onClick?: (event: React.MouseEvent<any>, data: Broadcast) => void;
}

export const NowAndNextCardPresenter: FC<NowAndNextCardPresenterProps> = ({ asset, cardContext, trackingData, onClick }) => {
    const navigate = useNavigate();
    const locationParamsPath = useDetailLocationParams();
    const { channels, getChannelById, fetchChannels, loading } = useChannels();
    const channel = getChannelById(asset.channelId);
    const { isGuest } = useAuth();
    const { isMini, updateMiniPlayerProps, closeMiniPlayer } = usePlayer();
    const { appLanguage } = useApp();

    const isEntitled = channel?.subscription ?? asset.isChannelSubscribed;

    const [isMobileDevice, setIsMobileDevice] = useState(() => matchIsMobileDevice());

    const { handleContentFocus, handleContentFocusOut, handleContentClick, trackContentPlay } = useYouboraContentTracking();

    const progress = (asset.bookmark * 100) / MILLISECONDS.toSeconds(asset.endTime - asset.startTime);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(pointer: coarse)');
        const handleChange = () => {
            setIsMobileDevice(matchIsMobileDevice());
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    const isInPlayer = () =>
        // eslint-disable-next-line no-restricted-globals
        location.pathname.includes('/play/live/');
    const handlePlayerNavigation = (forceLive: boolean = false) => {
        const playerUrl = getLivePlayerURL(asset.channelId, {
            ...(isInPlayer() || forceLive || progress === 0 ? { live: true } : {}),
        });

        /*
         * Handle player navigation based on current location:
         * - If already in player (/play/live/*), use 'replace' to avoid building history stack
         * - If outside player (e.g. homepage), use 'push' to allow normal back navigation
         * Why?: Prevents multiple player URLs in browser history when switching channels
         */

        const inPlayer = isInPlayer();
        if (inPlayer) {
            navigate(playerUrl, { replace: true, state: { channelSwitch: true } });
        } else {
            navigate(playerUrl);
        }
    };

    const getYouboraParams = (): ContentTrackingParams => ({
        section: trackingData['data-module-title'] || '',
        sectionOrder: trackingData['data-stripe-position'] || 1,
        column: trackingData['data-card-position'] || 1,
        row: 1,
        contentId: asset.id,
        contentLink: asset.id,
        contentLinkType: 'contentId',
    });

    const handleCardFocus = () => {
        handleContentFocus(getYouboraParams());
    };

    const handleCardFocusOut = () => {
        handleContentFocusOut();
    };

    const navigateToDetailsClosingPlayer = () => {
        if (isMini) {
            closeMiniPlayer();
        }
        const detailsLocationParams = isInPlayer() ? {} : locationParamsPath;
        navigate(getNowAndNextDetailsURL(asset, detailsLocationParams, appLanguage), { state: { closePlayer: true } });
    };

    const handleCardClick = (e: React.MouseEvent) => {
        if (!isGuest) {
            handleContentClick(getYouboraParams());
        }
        if (!isEntitled) {
            e.preventDefault();
            e.stopPropagation();
            navigateToDetailsClosingPlayer();
            return;
        }
        if (onClick) {
            onClick(e, asset as unknown as Broadcast);
        }
    };

    const handleMobileCardClick = (e: React.MouseEvent) => {
        if (!isEntitled) {
            e.preventDefault();
            navigateToDetailsClosingPlayer();
            return;
        }

        e.preventDefault();
        trackContentPlay(asset.id);

        if (isMini) {
            updateMiniPlayerProps(getMiniPlayerProps(asset, PlayingAssetType.LIVE));
        } else {
            handlePlayerNavigation(true);
        }
    };

    const onPlayIconClicked = (event) => {
        event.preventDefault();
        event.stopPropagation();

        handleContentClick(getYouboraParams());
        trackContentPlay(asset.id);

        if (isMini) {
            updateMiniPlayerProps(getMiniPlayerProps(asset, PlayingAssetType.LIVE));
        } else {
            handlePlayerNavigation(true);
        }
    };

    useEffect(() => {
        if ((!channels || !channels.length) && !loading && !channelsFetchInitiated.current) {
            channelsFetchInitiated.current = true;
            fetchChannels();
        }
    }, [channels, loading, fetchChannels]);

    const getCardPath = () => {
        if (!isEntitled) {
            return getNowAndNextDetailsURL(asset, isInPlayer() ? {} : locationParamsPath, appLanguage);
        }

        if (isInPlayer()) {
            return getLivePlayerURL(asset.channelId, {
                live: true,
            });
        }

        return isMobileDevice
            ? getLivePlayerURL(asset.channelId, {
                  ...(progress === 0 && { live: true }),
              })
            : getNowAndNextDetailsURL(asset, locationParamsPath, appLanguage);
    };

    const cover: StandardCover = {
        uuid: asset.id + asset.channelId,
        id: asset.id,
        title: asset.title,
        image: asset.image,
        landscapeImage: asset.image,
        channelImage: channel?.logo,
        channelName: channel?.name ?? asset.channelName,
        isFavorite: channel?.favorite,
        metaData: asset.nextBroadcastTitle,
        path: getCardPath(),
        circle: false,
        landscape: true,
        assetType: asset.type,
        rawData: asset,
        contentMarker: getNowAndNextCardContentMarkerValue(asset.isLive),
        progressBar: <LiveProgressIndicator startTime={asset.startTime} endTime={asset.endTime} />,
        renderPlayIcon: !isGuest && (channel?.subscription ?? asset.isChannelSubscribed) && !isMobileDevice,
        defaultRenderSecondLine: isMobileDevice,
        onPlayIconClicked,
        ...(isMobileDevice && { onClick: handleMobileCardClick }),
    };

    const contentTrackingProps = {
        onMouseEnter: handleCardFocus,
        onMouseLeave: handleCardFocusOut,
        onClick: handleCardClick,
    };

    return <Card variant={CardVariant.LANDSCAPE} context={cardContext} asset={cover} {...trackingData} {...contentTrackingProps} />;
};
